// Dungeon Runs data — everything the tool renders comes from this file.
//
// Seeded from classic Norrath geography as a starting point. As I run these
// on stream in EverQuest Legends, update zones/connections/dungeons here to
// match what's actually in the game — the UI recomputes paths automatically.

export type Zone = {
	id: string
	name: string
}

export type ZoneConnection = {
	from: string
	to: string
	// e.g. "Take the boat across the Ocean of Tears"
	note?: string
	// Shown as a warning on the route, e.g. "Deadly at night"
	danger?: string
}

export type StartingCity = {
	zoneId: string
	label: string
}

export type Race = {
	id: string
	name: string
	startingCities: StartingCity[]
}

export type Dungeon = {
	id: string // must match a zone id
	name: string
	levelRange: string
	blurb: string
}

export type RecordedRun = {
	raceId: string
	dungeonId: string
	youtubeUrl: string
	title: string
}

export const zones: Zone[] = [
	// Antonica — west
	{ id: 'qeynos', name: 'Qeynos' },
	{ id: 'qeynos-hills', name: 'Qeynos Hills' },
	{ id: 'blackburrow', name: 'Blackburrow' },
	{ id: 'everfrost', name: 'Everfrost Peaks' },
	{ id: 'halas', name: 'Halas' },
	{ id: 'permafrost', name: 'Permafrost Keep' },
	{ id: 'west-karana', name: 'West Karana' },
	{ id: 'north-karana', name: 'North Karana' },
	{ id: 'east-karana', name: 'East Karana' },
	{ id: 'south-karana', name: 'South Karana' },
	{ id: 'splitpaw', name: 'Infected Paw (Splitpaw)' },
	{ id: 'lake-rathetear', name: 'Lake Rathetear' },
	{ id: 'rathe-mountains', name: 'Rathe Mountains' },
	// Antonica — east
	{ id: 'highpass', name: 'Highpass Hold' },
	{ id: 'kithicor', name: 'Kithicor Forest' },
	{ id: 'rivervale', name: 'Rivervale' },
	{ id: 'misty-thicket', name: 'Misty Thicket' },
	{ id: 'runnyeye', name: 'Runnyeye Citadel' },
	{ id: 'gorge', name: 'Gorge of King Xorbb' },
	{ id: 'west-commonlands', name: 'West Commonlands' },
	{ id: 'befallen', name: 'Befallen' },
	{ id: 'east-commonlands', name: 'East Commonlands' },
	{ id: 'nektulos', name: 'Nektulos Forest' },
	{ id: 'neriak', name: 'Neriak' },
	{ id: 'lavastorm', name: 'Lavastorm Mountains' },
	{ id: 'najena', name: 'Najena' },
	{ id: 'soluseks-eye', name: "Solusek's Eye (Sol A)" },
	{ id: 'freeport', name: 'Freeport' },
	{ id: 'north-ro', name: 'North Ro' },
	{ id: 'oasis', name: 'Oasis of Marr' },
	{ id: 'south-ro', name: 'South Ro' },
	{ id: 'innothule', name: 'Innothule Swamp' },
	{ id: 'upper-guk', name: 'Upper Guk' },
	{ id: 'grobb', name: 'Grobb' },
	{ id: 'feerrott', name: 'The Feerrott' },
	{ id: 'oggok', name: 'Oggok' },
	// Odus
	{ id: 'erudin', name: 'Erudin' },
	// Faydwer
	{ id: 'greater-faydark', name: 'Greater Faydark' },
	{ id: 'crushbone', name: 'Crushbone' },
	{ id: 'felwithe', name: 'Felwithe' },
	{ id: 'butcherblock', name: 'Butcherblock Mountains' },
	{ id: 'kaladim', name: 'Kaladim' },
	{ id: 'dagnors-cauldron', name: "Dagnor's Cauldron" },
	{ id: 'unrest', name: 'The Estate of Unrest' },
	{ id: 'lesser-faydark', name: 'Lesser Faydark' },
	{ id: 'mistmoore', name: 'Castle Mistmoore' },
	{ id: 'steamfont', name: 'Steamfont Mountains' },
	{ id: 'akanon', name: "Ak'Anon" },
	// Kunark
	{ id: 'cabilis', name: 'Cabilis' },
	{ id: 'field-of-bone', name: 'Field of Bone' },
	{ id: 'kurns-tower', name: "Kurn's Tower" },
	{ id: 'lake-of-ill-omen', name: 'Lake of Ill Omen' },
	{ id: 'firiona-vie', name: 'Firiona Vie' },
]

export const connections: ZoneConnection[] = [
	// Qeynos side
	{ from: 'qeynos', to: 'qeynos-hills' },
	{ from: 'qeynos-hills', to: 'blackburrow' },
	{ from: 'blackburrow', to: 'everfrost' },
	{ from: 'everfrost', to: 'halas' },
	{ from: 'everfrost', to: 'permafrost' },
	{ from: 'qeynos-hills', to: 'west-karana' },
	// The Karanas
	{ from: 'west-karana', to: 'north-karana' },
	{ from: 'north-karana', to: 'east-karana' },
	{ from: 'north-karana', to: 'south-karana' },
	{ from: 'south-karana', to: 'splitpaw' },
	{ from: 'south-karana', to: 'lake-rathetear' },
	{ from: 'lake-rathetear', to: 'rathe-mountains' },
	{ from: 'rathe-mountains', to: 'feerrott' },
	// Southern Antonica
	{ from: 'feerrott', to: 'oggok' },
	{ from: 'feerrott', to: 'innothule' },
	{ from: 'innothule', to: 'grobb' },
	{ from: 'innothule', to: 'upper-guk' },
	{ from: 'innothule', to: 'south-ro' },
	{ from: 'south-ro', to: 'oasis' },
	{ from: 'oasis', to: 'north-ro' },
	{ from: 'north-ro', to: 'freeport' },
	// Eastern Antonica
	{ from: 'freeport', to: 'east-commonlands' },
	{ from: 'east-commonlands', to: 'west-commonlands' },
	{ from: 'west-commonlands', to: 'befallen' },
	{
		from: 'west-commonlands',
		to: 'kithicor',
		danger: 'Kithicor Forest is deadly at night — cross during game-time day',
	},
	{ from: 'kithicor', to: 'highpass' },
	{ from: 'kithicor', to: 'rivervale' },
	{ from: 'highpass', to: 'east-karana' },
	{ from: 'east-karana', to: 'gorge' },
	{ from: 'gorge', to: 'runnyeye' },
	{ from: 'runnyeye', to: 'misty-thicket' },
	{ from: 'misty-thicket', to: 'rivervale' },
	{ from: 'east-commonlands', to: 'nektulos' },
	{ from: 'nektulos', to: 'neriak' },
	{ from: 'nektulos', to: 'lavastorm' },
	{ from: 'lavastorm', to: 'najena' },
	{ from: 'lavastorm', to: 'soluseks-eye' },
	// Boats
	{
		from: 'erudin',
		to: 'qeynos',
		note: "Take the boat across Erud's Crossing",
	},
	{
		from: 'freeport',
		to: 'butcherblock',
		note: 'Take the boat across the Ocean of Tears',
	},
	{
		from: 'firiona-vie',
		to: 'butcherblock',
		note: 'Take the boat (verify the route in EQ Legends)',
	},
	// Faydwer
	{ from: 'butcherblock', to: 'kaladim' },
	{ from: 'butcherblock', to: 'greater-faydark' },
	{ from: 'butcherblock', to: 'dagnors-cauldron' },
	{ from: 'dagnors-cauldron', to: 'unrest' },
	{ from: 'greater-faydark', to: 'crushbone' },
	{ from: 'greater-faydark', to: 'felwithe' },
	{ from: 'greater-faydark', to: 'lesser-faydark' },
	{ from: 'lesser-faydark', to: 'mistmoore' },
	{ from: 'lesser-faydark', to: 'steamfont' },
	{ from: 'steamfont', to: 'akanon' },
	// Kunark
	{ from: 'cabilis', to: 'field-of-bone' },
	{ from: 'field-of-bone', to: 'kurns-tower' },
	{ from: 'cabilis', to: 'lake-of-ill-omen' },
	{ from: 'lake-of-ill-omen', to: 'firiona-vie' },
]

export const races: Race[] = [
	{
		id: 'barbarian',
		name: 'Barbarian',
		startingCities: [{ zoneId: 'halas', label: 'Halas' }],
	},
	{
		id: 'dark-elf',
		name: 'Dark Elf',
		startingCities: [{ zoneId: 'neriak', label: 'Neriak' }],
	},
	{
		id: 'dwarf',
		name: 'Dwarf',
		startingCities: [{ zoneId: 'kaladim', label: 'Kaladim' }],
	},
	{
		id: 'erudite',
		name: 'Erudite',
		startingCities: [{ zoneId: 'erudin', label: 'Erudin' }],
	},
	{
		id: 'gnome',
		name: 'Gnome',
		startingCities: [{ zoneId: 'akanon', label: "Ak'Anon" }],
	},
	{
		id: 'half-elf',
		name: 'Half Elf',
		startingCities: [
			{ zoneId: 'freeport', label: 'Freeport' },
			{ zoneId: 'qeynos', label: 'Qeynos' },
			{ zoneId: 'greater-faydark', label: 'Kelethin (Greater Faydark)' },
		],
	},
	{
		id: 'halfling',
		name: 'Halfling',
		startingCities: [{ zoneId: 'rivervale', label: 'Rivervale' }],
	},
	{
		id: 'high-elf',
		name: 'High Elf',
		startingCities: [{ zoneId: 'felwithe', label: 'Felwithe' }],
	},
	{
		id: 'human',
		name: 'Human',
		startingCities: [
			{ zoneId: 'freeport', label: 'Freeport' },
			{ zoneId: 'qeynos', label: 'Qeynos' },
		],
	},
	{
		id: 'iksar',
		name: 'Iksar',
		startingCities: [{ zoneId: 'cabilis', label: 'Cabilis' }],
	},
	{
		id: 'ogre',
		name: 'Ogre',
		startingCities: [{ zoneId: 'oggok', label: 'Oggok' }],
	},
	{
		id: 'troll',
		name: 'Troll',
		startingCities: [{ zoneId: 'grobb', label: 'Grobb' }],
	},
	{
		id: 'wood-elf',
		name: 'Wood Elf',
		startingCities: [
			{ zoneId: 'greater-faydark', label: 'Kelethin (Greater Faydark)' },
		],
	},
]

export const dungeons: Dungeon[] = [
	{
		id: 'blackburrow',
		name: 'Blackburrow',
		levelRange: '5–15',
		blurb: 'Gnoll den connecting Qeynos Hills and Everfrost. The classic first dungeon.',
	},
	{
		id: 'crushbone',
		name: 'Crushbone',
		levelRange: '5–15',
		blurb: 'Orc stronghold in Greater Faydark. Belts and shoulderpads for days.',
	},
	{
		id: 'befallen',
		name: 'Befallen',
		levelRange: '8–20',
		blurb: 'Undead crypt beneath the West Commonlands.',
	},
	{
		id: 'upper-guk',
		name: 'Upper Guk',
		levelRange: '5–25',
		blurb: 'Froglok warrens under Innothule Swamp.',
	},
	{
		id: 'runnyeye',
		name: 'Runnyeye Citadel',
		levelRange: '5–20',
		blurb: 'Goblin citadel between Misty Thicket and the Gorge of King Xorbb.',
	},
	{
		id: 'unrest',
		name: 'The Estate of Unrest',
		levelRange: '10–25',
		blurb: "Haunted estate on the shore of Dagnor's Cauldron.",
	},
	{
		id: 'najena',
		name: 'Najena',
		levelRange: '10–25',
		blurb: 'Lair of the wizard Najena in the Lavastorm Mountains.',
	},
	{
		id: 'splitpaw',
		name: 'Infected Paw (Splitpaw)',
		levelRange: '10–25',
		blurb: 'Gnoll caves in the middle of South Karana.',
	},
	{
		id: 'mistmoore',
		name: 'Castle Mistmoore',
		levelRange: '15–30',
		blurb: "The vampire Mayong Mistmoore's castle in Lesser Faydark.",
	},
	{
		id: 'soluseks-eye',
		name: "Solusek's Eye (Sol A)",
		levelRange: '15–30',
		blurb: 'Volcanic tunnels in the Lavastorm Mountains.',
	},
	{
		id: 'permafrost',
		name: 'Permafrost Keep',
		levelRange: '15–35',
		blurb: 'Frozen goblin keep in Everfrost — home of Lady Vox.',
	},
	{
		id: 'kurns-tower',
		name: "Kurn's Tower",
		levelRange: '5–15',
		blurb: 'Undead tower in the Field of Bone, right outside Cabilis.',
	},
]

// Add a run here after each stream to link the VOD on the tool page.
export const recordedRuns: RecordedRun[] = [
	// { raceId: 'human', dungeonId: 'blackburrow', youtubeUrl: 'https://youtube.com/watch?v=...', title: 'Fresh Human → Blackburrow' },
]
